import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Search, Download, RefreshCw, Undo2 } from "lucide-react";
import { api, downloadFile, currentRole } from "@/lib/api";
import { Pill, FilterSelect, DateInput, Pager, Stat, DARK_INPUT, inr, fmtDate, errMsg, useDebounced, useLatest } from "./shared";
import RefundDialog from "./RefundDialog";

const EMPTY = { q: "", status: "", gateway: "", payment_type: "", date_from: "", date_to: "" };

export default function PaymentManagement() {
  const [f, setF] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [refundFor, setRefundFor] = useState(null);
  const clean = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== ""));

  const q = useDebounced(f.q);
  const begin = useLatest();
  const load = useCallback(async () => {
    const isLatest = begin();
    try { const { data } = await api.get("/admin/manage/payments", { params: clean({ ...f, q, page, page_size: 20 }) }); if (isLatest()) setData(data); }
    catch (e) { if (isLatest()) { toast.error(errMsg(e, "Failed to load payments")); setData({ items: [], total: 0, summary: {} }); } }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.status, f.gateway, f.payment_type, f.date_from, f.date_to, q, page]);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (v) => { setPage(1); setF((x) => ({ ...x, [k]: v })); };

  const exportCsv = () => {
    const qs = new URLSearchParams(clean(f)).toString();
    downloadFile(`/admin/manage/payments/export.csv${qs ? `?${qs}` : ""}`, `karmivo_payments_${new Date().toISOString().slice(0, 10)}.csv`)
      .then(() => toast.success("CSV exported")).catch(() => toast.error("Export failed"));
  };
  const sync = async (p) => {
    try { await api.post(`/admin/manage/payments/${p.id}/sync`); toast.success("Synced with gateway"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const s = data?.summary || {};

  return (
    <div data-testid="payment-management-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-orange-400 font-bold">Admin · Finance</div>
          <h1 className="font-display text-3xl font-bold mt-1">Payment Management</h1>
          <p className="text-sm text-slate-400 mt-1">Every payment, refund and gateway attempt in one place. Totals count real Razorpay payments only.</p>
        </div>
        <Button className="bg-orange-500 hover:bg-orange-600" onClick={exportCsv} data-testid="pm-export-csv"><Download size={14} className="mr-2"/>Export CSV</Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Collected" value={inr(s.collected)} tone="text-emerald-400" testid="pm-stat-collected"/>
        <Stat label="Refunded" value={inr(s.refunded)} tone="text-red-400" testid="pm-stat-refunded"/>
        <Stat label="Net Revenue" value={inr(s.net)} testid="pm-stat-net"/>
        <Stat label="Failed Attempts" value={s.failed_attempts ?? 0} tone="text-amber-400" testid="pm-stat-failed"/>
      </div>
      <Tabs defaultValue="payments">
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="payments" data-testid="pm-tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="refunds" data-testid="pm-tab-refunds">Refunds</TabsTrigger>
          <TabsTrigger value="attempts" data-testid="pm-tab-attempts">Gateway Attempts</TabsTrigger>
        </TabsList>
        <TabsContent value="payments" className="mt-4">
          <div className="admin-card p-3 mb-4 flex flex-col md:flex-row md:flex-wrap gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"/>
              <Input value={f.q} onChange={(e) => set("q")(e.target.value)} placeholder="Booking code, customer, payment/order ID…" className={`${DARK_INPUT} pl-9 h-9`} data-testid="pm-search-input"/>
            </div>
            <FilterSelect value={f.status} onChange={set("status")} options={["successful", "partially_refunded", "refunded", "failed"]} placeholder="All statuses" testid="pm-filter-status"/>
            <FilterSelect value={f.gateway} onChange={set("gateway")} options={["razorpay", "razorpay_mock"]} placeholder="All gateways" testid="pm-filter-gateway"/>
            <FilterSelect value={f.payment_type} onChange={set("payment_type")} options={["ADVANCE", "BALANCE", "FULL"]} placeholder="All types" testid="pm-filter-type"/>
            <DateInput value={f.date_from} onChange={set("date_from")} testid="pm-filter-from"/>
            <DateInput value={f.date_to} onChange={set("date_to")} testid="pm-filter-to"/>
          </div>
          <PaymentsTable items={data?.items} isProd={data?.is_prod} onRefund={setRefundFor} onSync={sync}/>
          {data && <Pager page={page} pageSize={20} total={data.total} onPage={setPage} testid="pm-pager"/>}
        </TabsContent>
        <TabsContent value="refunds" className="mt-4"><SimpleList url="/admin/manage/refunds" testid="pm-refunds" cols={[["Refund ID", "gateway_refund_id"], ["Booking", "booking_code"], ["Amount", "amount", inr], ["Reason", "reason"], ["Status", "status", (v) => <Pill value={v}/>], ["At", "created_at", fmtDate]]}/></TabsContent>
        <TabsContent value="attempts" className="mt-4"><SimpleList url="/admin/manage/payment-attempts" testid="pm-attempts" cols={[["Order ID", "gateway_order_id"], ["Booking", "booking_code"], ["Type", "payment_type"], ["Amount", "amount", inr], ["Status", "status", (v) => <Pill value={v}/>], ["At", "created_at", fmtDate]]}/></TabsContent>
      </Tabs>
      {refundFor && <RefundDialog p={refundFor} onClose={() => setRefundFor(null)} onDone={() => { setRefundFor(null); load(); }}/>}
    </div>
  );
}

function PaymentsTable({ items, isProd, onRefund, onSync }) {
  if (!items) return <div className="text-slate-500 text-sm p-6">Loading…</div>;
  if (!items.length) return <div className="admin-card p-10 text-center text-slate-500" data-testid="pm-empty">No payments recorded yet. Payments appear here once online payments are switched on.</div>;
  return (
    <div className="admin-card overflow-x-auto">
      <table className="w-full text-sm min-w-[860px]">
        <thead className="bg-slate-800/70 text-slate-400 text-[10px] uppercase tracking-widest">
          <tr><th className="p-3 text-left">Payment</th><th className="text-left">Booking</th><th className="text-left">Type</th><th className="text-right">Amount</th><th className="text-right">Refunded</th><th className="text-left pl-4">Status</th><th className="text-left">Paid</th><th></th></tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr key={p.id} className="border-t border-slate-800" data-testid={`pm-row-${p.id}`}>
              <td className="p-3 font-mono text-xs">{p.gateway_payment_id}{p.is_mock && <span className="ml-2 text-[10px] text-amber-400">TEST</span>}</td>
              <td><div className="font-mono text-orange-300 text-xs">{p.booking_code || "—"}</div><div className="text-[11px] text-slate-500">{p.customer_name}</div></td>
              <td className="text-xs text-slate-300">{p.payment_type}</td>
              <td className="text-right font-mono font-bold text-emerald-400">{inr(p.amount)}</td>
              <td className="text-right font-mono text-red-300">{p.refunded_amount ? inr(p.refunded_amount) : "—"}</td>
              <td className="pl-4"><Pill value={p.status}/></td>
              <td className="text-xs text-slate-400">{fmtDate(p.paid_at)}</td>
              <td className="pr-3"><div className="flex gap-1 justify-end">
                {!p.is_mock && currentRole() === "super_admin" && <Button size="sm" variant="ghost" className="text-slate-400 h-8" onClick={() => onSync(p)} data-testid={`pm-sync-${p.id}`}><RefreshCw size={13}/></Button>}
                {currentRole() === "super_admin" && ["successful", "partially_refunded"].includes(p.status) && !(p.is_mock && isProd) && <Button size="sm" variant="outline" className="border-red-500/40 bg-transparent text-red-300 hover:bg-red-500/10 h-8" onClick={() => onRefund(p)} data-testid={`pm-refund-${p.id}`}><Undo2 size={13} className="mr-1"/>Refund</Button>}
              </div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SimpleList({ url, cols, testid }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get(url).then((r) => setItems(r.data)).catch(() => setItems([])); }, [url]);
  if (!items) return <div className="text-slate-500 text-sm p-6">Loading…</div>;
  return (
    <div className="admin-card overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm min-w-[720px]">
        <thead className="bg-slate-800/70 text-slate-400 text-[10px] uppercase tracking-widest"><tr>{cols.map(([h]) => <th key={h} className="p-3 text-left">{h}</th>)}</tr></thead>
        <tbody>
          {items.map((r) => <tr key={r.id} className="border-t border-slate-800">{cols.map(([h, k, fmt]) => <td key={h} className="p-3 text-xs text-slate-300">{fmt ? fmt(r[k]) : (r[k] || "—")}</td>)}</tr>)}
          {items.length === 0 && <tr><td colSpan={cols.length} className="p-8 text-center text-slate-500 text-sm" data-testid={`${testid}-empty`}>Nothing here yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
